import "./App.css";
import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import TopNav from "./components/TopNav";
import EnvBanner from "./components/EnvBanner";
import PlaceholderPage from "./components/PlaceholderPage";

import Login          from "./pages/Login";
import Dashboard      from "./pages/Dashboard";
import FlaggedItems   from "./pages/FlaggedItems";
import TendersList    from "./pages/TendersList";
import TenderCreate   from "./pages/TenderCreate";
import TenderDetail   from "./pages/TenderDetail";
import Materials      from "./pages/Materials";
import MaterialEdit   from "./pages/MaterialEdit";
import LabourRates    from "./pages/LabourRates";
import LabourRateEdit from "./pages/LabourRateEdit";
import Library        from "./pages/Library";
import LibraryEdit    from "./pages/LibraryEdit";
import PrelimLibrary  from "./pages/PrelimLibrary";
import PrelimLibraryEdit from "./pages/PrelimLibraryEdit";
import ProvsumLibrary from "./pages/ProvsumLibrary";
import ProvsumLibraryEdit from "./pages/ProvsumLibraryEdit";
import AOLibrary      from "./pages/AOLibrary";
import AOLibraryEdit  from "./pages/AOLibraryEdit";
import SummaryLibraryAdmin from "./pages/SummaryLibraryAdmin";
import Dictionary     from "./pages/Dictionary";
import Templates      from "./pages/Templates";
import TemplateEdit   from "./pages/TemplateEdit";
import EmailTemplates from "./pages/EmailTemplates";
import Settings       from "./pages/Settings";
// import TenderChecksConfig from "./pages/TenderChecksConfig";  // retired — route now redirects to Benchmarks
import Insights       from "./pages/Insights";

function Shell({ children }) {
  return (
    <div className="app-shell min-h-screen">
      <EnvBanner />
      <TopNav />
      <main>{children}</main>
    </div>
  );
}

function Protected({ children }) {
  return (
    <ProtectedRoute>
      <Shell>{children}</Shell>
    </ProtectedRoute>
  );
}

/**
 * AppRouter — detects #session_id=... synchronously during render to
 * prevent race conditions with ProtectedRoute / AuthContext.
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      <Route path="/"          element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/flagged"   element={<Protected><FlaggedItems /></Protected>} />

      <Route path="/tenders"           element={<Protected><TendersList /></Protected>} />
      <Route path="/tenders/new"       element={<Protected><TenderCreate /></Protected>} />
      <Route path="/tenders/:id"       element={<Protected><TenderDetail /></Protected>} />

      <Route path="/materials"          element={<Protected><Materials /></Protected>} />
      <Route path="/materials/new"      element={<Protected><MaterialEdit /></Protected>} />
      <Route path="/materials/:id"      element={<Protected><MaterialEdit /></Protected>} />

      <Route path="/labour-rates"       element={<Protected><LabourRates /></Protected>} />
      <Route path="/labour-rates/new"   element={<Protected><LabourRateEdit /></Protected>} />
      <Route path="/labour-rates/:id"   element={<Protected><LabourRateEdit /></Protected>} />

      <Route path="/library"            element={<Protected><Library /></Protected>} />
      <Route path="/library/new"        element={<Protected><LibraryEdit /></Protected>} />
      <Route path="/library/:id"        element={<Protected><LibraryEdit /></Protected>} />

      <Route path="/prelim-library"      element={<Protected><PrelimLibrary /></Protected>} />
      <Route path="/prelim-library/new"  element={<Protected><PrelimLibraryEdit /></Protected>} />
      <Route path="/prelim-library/:id"  element={<Protected><PrelimLibraryEdit /></Protected>} />

      <Route path="/provsum-library"     element={<Protected><ProvsumLibrary /></Protected>} />
      <Route path="/provsum-library/new" element={<Protected><ProvsumLibraryEdit /></Protected>} />
      <Route path="/provsum-library/:id" element={<Protected><ProvsumLibraryEdit /></Protected>} />

      <Route path="/ao-library"          element={<Protected><AOLibrary /></Protected>} />
      <Route path="/ao-library/new"      element={<Protected><AOLibraryEdit /></Protected>} />
      <Route path="/ao-library/:id"      element={<Protected><AOLibraryEdit /></Protected>} />

      {/* LT-12.5 — Summary Cover libraries */}
      <Route path="/notes-library"       element={<Protected><SummaryLibraryAdmin mode="things-to-note" /></Protected>} />
      <Route path="/exclusions-library"  element={<Protected><SummaryLibraryAdmin mode="whats-excluded" /></Protected>} />

      <Route path="/settings/dictionary" element={<Protected><Dictionary /></Protected>} />
      {/* LT-Estimate-UX-01 follow-up — Tender Checks retired. Kept as a
          301-style redirect so any bookmarks or external links continue
          to work. Day Rate Check constants now live under the unified
          Benchmarks & Defaults tab. */}
      <Route path="/settings/tender-checks-config" element={<Navigate to="/settings?tab=benchmarks" replace />} />

      <Route path="/templates"          element={<Protected><Templates /></Protected>} />
      <Route path="/templates/:id"      element={<Protected><TemplateEdit /></Protected>} />
      <Route path="/email-templates"    element={<Protected><EmailTemplates /></Protected>} />

      <Route path="/documents"          element={<Protected><PlaceholderPage
        title="Documents" subtitle="T&Cs, cover letters, FMB contracts"
        phase="LT-14"
        description="Workspace-level document templates and uploads will be built here."
      /></Protected>} />

      <Route path="/reports"            element={<Protected><PlaceholderPage
        title="Reports" subtitle="Allowance vs Actual, pipeline analytics"
        phase="LT-20"
        description="Reports area is reserved. Analytical reporting lands here."
      /></Protected>} />

      <Route path="/settings"           element={<Protected><Settings /></Protected>} />

      <Route path="/insights"           element={<Protected><Insights /></Protected>} />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster
          position="top-right"
          theme="light"
          richColors
          closeButton
          toastOptions={{
            style: { fontFamily: "IBM Plex Sans, sans-serif", fontSize: 13 }
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
